import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    getOpenApiBulkUpdateVariantMediaUrl,
    getOpenApiDeletePermanentlyProductVariantsUrl,
    getOpenApiSetVariantsRequiresMarkingUrl,
    type OpenApiApplyPageVariantToProductCardsRequest,
    type OpenApiApplyPageVariantToProductCardsResponseSuccess,
    OpenApiBulkUpdateVariantMediaMethod,
    type OpenApiBulkUpdateVariantMediaRequest,
    type OpenApiBulkUpdateVariantMediaResponseSuccess,
    type OpenApiChangeProductVariantsStatusesRequest,
    type OpenApiChangeProductVariantsStatusesResponseSuccess,
    type OpenApiCreateProductRequest,
    type OpenApiCreateProductResponseSuccess,
    type OpenApiDeleteExternalIDsByRequestResponseSuccess,
    OpenApiDeletePermanentlyProductVariantsMethod,
    type OpenApiDeletePermanentlyProductVariantsRequest,
    type OpenApiDeletePermanentlyProductVariantsResponseSuccess,
    OpenApiGenerateAISeoForVariantMethod,
    type OpenApiGenerateAISeoForVariantResponseSuccess,
    OpenApiGenerateAISeoForVariantUrl,
    type OpenApiGetAllProductsCountsRequest,
    type OpenApiGetAllProductsCountsResponseSuccess,
    type OpenApiGetArchivedProductVariantsByRequestRequest,
    type OpenApiGetArchivedProductVariantsByRequestResponseSuccess,
    OpenApiGetLightProductCardsByRequestMethod,
    type OpenApiGetLightProductCardsByRequestRequest,
    type OpenApiGetLightProductCardsByRequestResponseSuccess,
    OpenApiGetLightProductCardsByRequestUrl,
    type OpenApiGetProductCardsByRequestRequest,
    type OpenApiGetProductCardsByRequestResponseSuccess,
    type OpenApiGetProductCardsCountByRequestRequest,
    type OpenApiGetProductCardsCountByRequestResponseSuccess,
    type OpenApiGetProductResponseSuccess,
    type OpenApiGetProductsByRequestRequest,
    OpenApiGetProductsCardSortOrderSettingsMethod,
    type OpenApiGetProductsCardSortOrderSettingsRequest,
    type OpenApiGetProductsCardSortOrderSettingsResponseSuccess,
    OpenApiGetProductsCardSortOrderSettingsUrl,
    type OpenApiGetProductVariantMediaPathParameters,
    type OpenApiGetProductVariantMediaResponseSuccess,
    type OpenApiGetProductVariantPathParameters,
    type OpenApiGetProductVariantResponseSuccess,
    type OpenApiGetProductVariantsByRequestRequest,
    type OpenApiGetProductVariantsByRequestResponseSuccess,
    OpenApiGetProductVariantsCountMethod,
    type OpenApiGetProductVariantsCountRequest,
    type OpenApiGetProductVariantsCountResponseSuccess,
    OpenApiGetProductVariantsCountUrl,
    type OpenApiGetProductVariantsGroupingRequest,
    type OpenApiGetProductVariantsGroupingResponseSuccess,
    type OpenApiGetWarehousesRequest,
    type OpenApiGetWarehousesResponseSuccess,
    type OpenApiMergeProductsPreValidateRequest,
    type OpenApiMergeProductsPreValidateResponseSuccess,
    type OpenApiMergeProductsV2Request,
    type OpenApiMergeProductsV2ResponseSuccess,
    type OpenApiMergeProductsValidateRequest,
    type OpenApiMergeProductsValidateResponseSuccess,
    OpenApiPatchProductsCardSortOrderSettingsMethod,
    type OpenApiPatchProductsCardSortOrderSettingsRequest,
    type OpenApiPatchProductsCardSortOrderSettingsResponseSuccess,
    OpenApiPatchProductsCardSortOrderSettingsUrl,
    type OpenApiSchemaAggregatedProductCardListItem,
    type OpenApiSchemaDeleteExternalIDsByQueryRequest,
    type OpenApiSchemaProductCharacteristic,
    type OpenApiSchemaProductVariant,
    type OpenApiSchemaProductVariantListItem,
    type OpenApiSchemaProductVariantWithSettings,
    OpenApiSetVariantsRequiresMarkingMethod,
    type OpenApiSetVariantsRequiresMarkingRequest,
    type OpenApiSetVariantsRequiresMarkingResponseSuccess,
    type OpenApiUnmergeProductRequest,
    type OpenApiUnmergeProductResponseSuccess,
    type OpenApiUnmergeProductsRequest,
    type OpenApiUnmergeProductsResponseSuccess,
    type OpenApiUpdateCardVariantsInBulkPathParameters,
    type OpenApiUpdateCardVariantsInBulkRequest,
    type OpenApiUpdateCardVariantsInBulkResponseSuccess,
    type OpenApiUpdateProductsCategoriesV2Request,
    type OpenApiUpdateProductsCategoriesV2ResponseSuccess,
    type OpenApiUpdateProductVariantPathParameters,
    type OpenApiUpdateProductVariantRequest,
    type OpenApiUpdateProductVariantResponseSuccess,
    type OpenApiUpdateProductVariantsInBulkPathParameters,
    type OpenApiUpdateProductVariantsInBulkRequest,
    type OpenApiUpdateProductVariantsInBulkResponseSuccess,
} from '#shared/api/schema';
import { InvalidateGridApiHelper } from '#shared/lib/requests';
import { getUpdatedPriceIndexSummary } from '../lib/getUpdatedPriceIndexSummary';
export const VariantTag = 'Variant' as const;
export const ProductVariantTag = 'ProductVariant' as const;
export const ProductTag = 'Product' as const;
export const CardTag = 'CardGrid' as const;
export const ProductCardTag = 'CardTemp' as const;

const VariantsGridTag = 'VariantsGrid' as const;
const ArchivedVariantsGridTag = 'ArchivedVariantsGrid' as const;
const CardsGridTag = 'CardsGrid' as const;

const VariantsCountTag = 'VariantsCount' as const;
const CardsCountTag = 'CardsCount' as const;

export const productGridTagInvalidator = new InvalidateGridApiHelper<
    typeof ProductTag,
    {
        payload: OpenApiGetProductsByRequestRequest;
    }
>(ProductTag, 'productGrid');

export const cardGridTagInvalidator = new InvalidateGridApiHelper<
    typeof CardTag,
    {
        payload: OpenApiGetProductCardsByRequestRequest;
    }
>(CardTag, 'cardGrid');

export const variantGridTagInvalidator = new InvalidateGridApiHelper<
    typeof VariantTag,
    {
        payload: OpenApiGetProductVariantsByRequestRequest;
    }
>(VariantTag, 'variantGrid');

export const archivedVariantGridTagInvalidator = new InvalidateGridApiHelper<
    typeof ArchivedVariantsGridTag,
    {
        payload: OpenApiGetArchivedProductVariantsByRequestRequest;
    }
>(ArchivedVariantsGridTag, 'variantGrid');

export const api = createApi({
    name: 'entities/product/api/api',
    tagTypes: [
        VariantTag,
        ProductTag,
        ProductVariantTag,
        CardTag,
        ProductCardTag,
        VariantsGridTag,
        ArchivedVariantsGridTag,
        CardsGridTag,
        VariantsCountTag,
        CardsCountTag,
    ],
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getProduct: builder.query<OpenApiGetProductResponseSuccess, { productId: string }>({
                query: ({ productId }) => {
                    return {
                        url: '/products/' + productId,
                        method: 'GET',
                    };
                },
                // Тэги для получения продукта
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return result.variants.reduce<{ type: 'Product' | 'ProductVariant'; id: string }[]>(
                        (tags, variant) => {
                            // Тэг на вариант в полученном продукте
                            tags.push({ type: ProductVariantTag, id: variant.id });

                            return tags;
                        },
                        [
                            // Общий тэг на ручку (для инвалидации всех запросов ручки)
                            { type: ProductTag, id: 'getProduct' },
                            // Тег на полученный продукт
                            { type: ProductTag, id: result.id },
                        ]
                    );
                },
            }),
            getCardsForGrid: builder.query<
                OpenApiGetProductCardsByRequestResponseSuccess,
                { payload: OpenApiGetProductCardsByRequestRequest; forceRefetch?: boolean }
            >({
                query: ({ payload }) => {
                    return {
                        url: '/cards/search',
                        method: 'POST',
                        body: payload,
                    };
                },
                transformResponse: (
                    response: OpenApiGetProductCardsByRequestResponseSuccess
                ): OpenApiGetProductCardsByRequestResponseSuccess => {
                    if (response?.cards) {
                        return response;
                    }

                    return {
                        cards: [],
                        total_count: 0,
                    };
                },
                forceRefetch: ({ currentArg }) => Boolean(currentArg?.forceRefetch),
                providesTags: (result, error, args) => {
                    if (error || !result) {
                        return [];
                    }

                    return [CardsGridTag, ...cardGridTagInvalidator.provideHashTagForGridEndpoint(args)];
                },
                onQueryStarted() {
                    api.util.invalidateTags(cardGridTagInvalidator.invalidateHashTagForGridEndpoint());
                },
            }),
            getLightProductCards: builder.query<
                OpenApiGetLightProductCardsByRequestResponseSuccess,
                { payload: OpenApiGetLightProductCardsByRequestRequest; forceRefetch?: boolean }
            >({
                query: ({ payload }) => {
                    return {
                        url: OpenApiGetLightProductCardsByRequestUrl,
                        method: OpenApiGetLightProductCardsByRequestMethod,
                        body: payload,
                    };
                },
                transformResponse: (
                    response: OpenApiGetLightProductCardsByRequestResponseSuccess
                ): OpenApiGetLightProductCardsByRequestResponseSuccess => {
                    if (response?.cards) {
                        return response;
                    }

                    return {
                        cards: [],
                        total_count: 0,
                    };
                },
                forceRefetch: ({ currentArg }) => Boolean(currentArg?.forceRefetch),
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        CardsGridTag,
                        ...result.cards.flatMap((card) =>
                            card.variants.map((variant) => {
                                return { type: ProductVariantTag, id: variant.id };
                            })
                        ),
                    ];
                },
            }),
            getVariantsForGrid: builder.query<
                OpenApiGetProductVariantsByRequestResponseSuccess,
                { payload: OpenApiGetProductVariantsByRequestRequest; forceRefetch?: boolean }
            >({
                query: ({ payload }) => {
                    return {
                        url: '/variants/search',
                        method: 'POST',
                        body: payload,
                    };
                },
                transformResponse: (
                    response: OpenApiGetProductVariantsByRequestResponseSuccess
                ): OpenApiGetProductVariantsByRequestResponseSuccess => {
                    if (response.items) {
                        return response;
                    }

                    // Если items не пришли (null бывает), то считаем что записей нет
                    return {
                        items: [],
                        total_count: 0,
                    };
                },
                forceRefetch: ({ currentArg }) => Boolean(currentArg?.forceRefetch),
                providesTags: (result, error, args) => {
                    if (error || !result) {
                        return [];
                    }

                    return [VariantsGridTag, ...variantGridTagInvalidator.provideHashTagForGridEndpoint(args)];
                },
                onQueryStarted() {
                    api.util.invalidateTags(variantGridTagInvalidator.invalidateHashTagForGridEndpoint());
                },
            }),

            getArchivedVariantsForGrid: builder.query<
                OpenApiGetArchivedProductVariantsByRequestResponseSuccess,
                { payload: OpenApiGetArchivedProductVariantsByRequestRequest }
            >({
                query: ({ payload }) => {
                    return {
                        url: '/variants/archived/search',
                        method: 'POST',
                        body: payload,
                    };
                },
                transformResponse: (
                    response: OpenApiGetArchivedProductVariantsByRequestResponseSuccess
                ): OpenApiGetArchivedProductVariantsByRequestResponseSuccess => {
                    if (response.items) {
                        return response;
                    }

                    // Если items не пришли (null бывает), то считаем что записей нет
                    return {
                        items: [],
                        total_count: 0,
                    };
                },
                providesTags: (result, error, args) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        ArchivedVariantsGridTag,
                        ...archivedVariantGridTagInvalidator.provideHashTagForGridEndpoint(args),
                    ];
                },
                onQueryStarted() {
                    api.util.invalidateTags(archivedVariantGridTagInvalidator.invalidateHashTagForGridEndpoint());
                },
            }),
            getProductCountAll: builder.query<
                OpenApiGetAllProductsCountsResponseSuccess,
                { payload?: OpenApiGetAllProductsCountsRequest }
            >({
                query: ({ payload = {} }) => {
                    return {
                        url: '/counts/all',
                        method: 'POST',
                        body: payload,
                    };
                },
                providesTags: [VariantsCountTag, CardsCountTag],
            }),
            getCardsCount: builder.query<
                OpenApiGetProductCardsCountByRequestResponseSuccess,
                OpenApiGetProductCardsCountByRequestRequest
            >({
                query: (body) => {
                    return {
                        url: '/cards/count',
                        method: 'POST',
                        body,
                    };
                },
                providesTags: [CardsCountTag],
            }),
            getVariantsCount: builder.query<
                OpenApiGetProductVariantsCountResponseSuccess,
                OpenApiGetProductVariantsCountRequest
            >({
                query: (body) => {
                    return {
                        url: OpenApiGetProductVariantsCountUrl,
                        method: OpenApiGetProductVariantsCountMethod,
                        body,
                    };
                },
                providesTags: [VariantsCountTag],
            }),
            getProductCards: builder.query<
                OpenApiGetProductVariantsGroupingResponseSuccess,
                { id: string; variantId: string; payload: OpenApiGetProductVariantsGroupingRequest }
            >({
                query: ({ id, payload }) => {
                    return {
                        url: `/products/${id}/cards`,
                        method: 'POST',
                        body: payload,
                    };
                },
                providesTags: (result, error, args) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        { type: ProductCardTag, id: args.variantId },
                        { type: ProductCardTag, id: 'allProducts' },
                    ];
                },
            }),
            getProductVariantById: builder.query<
                OpenApiGetProductVariantResponseSuccess,
                OpenApiGetProductVariantPathParameters
            >({
                query: ({ id }) => {
                    return {
                        url: `/variants/${id}`,
                        method: 'GET',
                    };
                },
                providesTags: (result, error, { id }) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        // Общий тэг на ручку
                        { type: VariantTag, id: 'getProductVariantById' },
                        // Тэг варианта
                        { type: VariantTag, id },
                        // Тэг продукта
                        { type: ProductTag, id: result.product_id },
                    ];
                },
            }),
            getProductVariantMedia: builder.query<
                OpenApiGetProductVariantMediaResponseSuccess,
                OpenApiGetProductVariantMediaPathParameters
            >({
                query: ({ id }) => {
                    return {
                        url: `/variants/${id}/media`,
                        method: 'GET',
                    };
                },
            }),
            updateProductVariant: builder.mutation<
                OpenApiUpdateProductVariantResponseSuccess,
                { payload: OpenApiUpdateProductVariantRequest; params: OpenApiUpdateProductVariantPathParameters }
            >({
                query: ({ params: { id }, payload }) => {
                    return {
                        url: `/variants/${id}`,
                        method: 'PATCH',
                        body: payload,
                    };
                },
                invalidatesTags: (_, error, args) => {
                    if (error) {
                        return [];
                    }

                    return [
                        // Обновляем ручку получения продуктов для грида
                        ...productGridTagInvalidator.getTagsToInvalidate(),
                        // Общий тэг на ручку для инвалидации всех запросов продуктов
                        { type: ProductTag, id: 'getProduct' },
                        { type: ProductCardTag, id: args.params.id },
                        ...(args.payload.name !== undefined || args.payload.status !== undefined
                            ? [VariantsCountTag]
                            : []),
                    ];
                },
                async onQueryStarted(_, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data: updatedVariant } = queryFulfilledResult;

                    updateVariantCache(updatedVariant);
                },
            }),
            updateProductVariantsStatuses: builder.mutation<
                OpenApiChangeProductVariantsStatusesResponseSuccess,
                OpenApiChangeProductVariantsStatusesRequest
            >({
                query: (payload) => {
                    return {
                        url: '/variants/statuses',
                        method: 'PUT',
                        body: payload,
                    };
                },
                invalidatesTags: (_result, error, payload) => {
                    if (error) {
                        return [];
                    }

                    if (payload.target_status === 'ARCHIVED') {
                        return [
                            CardsGridTag,
                            VariantsGridTag,
                            ArchivedVariantsGridTag,
                            VariantsCountTag,
                            CardsCountTag,
                        ];
                    }

                    // Таги инвалидации для ручек получения вариантов в продукте
                    return [
                        ...(payload.query?.variant_ids?.map((id) => {
                            return { type: ProductVariantTag, id };
                        }) ?? []),
                        // Если было использовано массовое выделение - обнуляем кэш
                        ...(payload.query?.variant_ids?.length
                            ? []
                            : [
                                  { type: ProductTag, id: 'getProduct' },
                                  { type: VariantTag, id: 'getProductVariantById' },
                                  VariantsGridTag,
                                  CardsGridTag,
                                  ArchivedVariantsGridTag,
                              ]),
                        { type: ProductCardTag, id: 'allProducts' },
                        VariantsCountTag,
                        CardsCountTag,
                        // Если восстанавливаем вариант - полностью перезапрашиваем варианты и карточки
                        ...(payload.query?.statuses?.includes('ARCHIVED') ? [VariantsGridTag, CardsGridTag] : []),
                    ];
                },
                async onQueryStarted(payload, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const updatedIds = new Set(payload.query?.variant_ids ?? []);

                    // Обновляем ручку получения варианта
                    updatedIds.forEach((id) => {
                        api.endpoints.getProductVariantById.updateQueryData({ id }, (draft) => {
                            if (draft) {
                                draft.status = payload.target_status;
                            }
                        });
                    });

                    const onVariantUpdate = (currentVariant: OpenApiSchemaProductVariantListItem) => {
                        if (updatedIds.has(currentVariant.id)) {
                            return { ...currentVariant, status: payload.target_status };
                        }

                        return currentVariant;
                    };

                    // Обновляем данные в текущем запросе ручки для грида
                    if (variantGridTagInvalidator.lastArgs) {
                        updateVariantsInGridProductCache(variantGridTagInvalidator.lastArgs, {
                            onVariantUpdate,
                        });
                    }

                    if (archivedVariantGridTagInvalidator.lastArgs) {
                        updateVariantsInGridProductCache(archivedVariantGridTagInvalidator.lastArgs, {
                            onVariantUpdate,
                        });
                    }

                    // Обновляем данные в текущем запросе ручки для грида
                    if (productGridTagInvalidator.lastArgs) {
                        updateVariantsInGridProductCache(productGridTagInvalidator.lastArgs, {
                            onVariantUpdate,
                        });
                    }

                    // Обновляем данные в текущем запросе ручки для грида
                    if (cardGridTagInvalidator.lastArgs) {
                        updateVariantsInGridProductCache(cardGridTagInvalidator.lastArgs, {
                            onVariantUpdate,
                        });
                    }
                },
            }),
            setVariantsRequiresMarking: builder.mutation<
                OpenApiSetVariantsRequiresMarkingResponseSuccess,
                OpenApiSetVariantsRequiresMarkingRequest
            >({
                query: (payload) => {
                    return {
                        url: getOpenApiSetVariantsRequiresMarkingUrl(),
                        method: OpenApiSetVariantsRequiresMarkingMethod,
                        body: payload,
                    };
                },
                invalidatesTags: (_result, error, payload) => {
                    if (error) {
                        return [];
                    }

                    // Если грид отфильтрован по требованию маркировки, смена флага меняет
                    // состав строк грида — кэш гридов нужно сбросить даже при точечном выборе,
                    // иначе изменённый вариант останется висеть в гриде устаревшим.
                    const isMarkingFiltered = payload.query?.requires_marking !== undefined;

                    return [
                        ...(payload.query?.variant_ids?.map((id) => {
                            return { type: ProductVariantTag, id };
                        }) ?? []),
                        // Если было использовано массовое выделение или грид отфильтрован
                        // по маркировке - обнуляем кэш гридов
                        ...(payload.query?.variant_ids?.length && !isMarkingFiltered
                            ? []
                            : [
                                  { type: ProductTag, id: 'getProduct' },
                                  { type: VariantTag, id: 'getProductVariantById' },
                                  VariantsGridTag,
                                  CardsGridTag,
                              ]),
                        { type: ProductCardTag, id: 'allProducts' },
                    ];
                },
            }),
            deleteProductVariantExternalIDs: builder.mutation<
                OpenApiDeleteExternalIDsByRequestResponseSuccess,
                OpenApiSchemaDeleteExternalIDsByQueryRequest
            >({
                query: (payload) => {
                    return {
                        url: '/variants/external-ids',
                        method: 'DELETE',
                        body: payload,
                    };
                },
                invalidatesTags: (_result, error, _payload) => {
                    if (error) {
                        return [];
                    }

                    // Таги инвалидации для ручек получения вариантов в продукте
                    return [
                        { type: ProductTag, id: 'getProduct' },
                        { type: VariantTag, id: 'getProductVariantById' },
                        { type: ProductCardTag, id: 'allProducts' },
                    ];
                },
            }),
            deletePermanentlyProductVariants: builder.mutation<
                OpenApiDeletePermanentlyProductVariantsResponseSuccess,
                { body: OpenApiDeletePermanentlyProductVariantsRequest }
            >({
                query: ({ body }) => {
                    return {
                        url: getOpenApiDeletePermanentlyProductVariantsUrl(),
                        method: OpenApiDeletePermanentlyProductVariantsMethod,
                        body,
                    };
                },
                invalidatesTags: (_result, error, payload) => {
                    if (error) {
                        return [];
                    }

                    return [
                        ArchivedVariantsGridTag,
                        VariantsGridTag,
                        CardsGridTag,
                        VariantsCountTag,
                        CardsCountTag,
                        { type: ProductCardTag, id: 'allProducts' },
                        { type: ProductTag, id: 'getProduct' },
                        { type: VariantTag, id: 'getProductVariantById' },
                        ...(payload.body.query?.variant_ids ?? []).map((id) => {
                            return { type: ProductVariantTag, id };
                        }),
                    ];
                },
                async onQueryStarted(payload, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const selectedIds = new Set(payload.body.query?.variant_ids ?? []);
                    const excludedIds = new Set(payload.body.query?.exclude_variant_ids ?? []);
                    const isSelectAll = !selectedIds.size;

                    const onVariantUpdate = (currentVariant: OpenApiSchemaProductVariantListItem) => {
                        if (isSelectAll) {
                            return excludedIds.has(currentVariant.id) ? currentVariant : undefined;
                        }

                        return selectedIds.has(currentVariant.id) ? undefined : currentVariant;
                    };

                    if (archivedVariantGridTagInvalidator.lastArgs) {
                        updateVariantsInGridProductCache(archivedVariantGridTagInvalidator.lastArgs, {
                            onVariantUpdate,
                        });
                    }
                },
            }),
            updateProductsCategories: builder.mutation<
                OpenApiUpdateProductsCategoriesV2ResponseSuccess,
                OpenApiUpdateProductsCategoriesV2Request
            >({
                query: (payload) => {
                    return {
                        url: '/products/categories/v2',
                        method: 'PUT',
                        body: payload,
                    };
                },
                invalidatesTags: (_result, error, payload) => {
                    if (error) {
                        return [];
                    }

                    return payload.query?.variant_ids?.length
                        ? []
                        : [
                              { type: ProductTag, id: 'getProduct' },
                              { type: VariantTag, id: 'getProductVariantById' },
                              VariantsGridTag,
                              CardsGridTag,
                              ArchivedVariantsGridTag,
                          ];
                },
                async onQueryStarted(payload, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const updatedIds = new Set(payload.query?.variant_ids ?? []);
                    const updatedCategories = new Set([...payload.add_category_ids, ...payload.remove_category_ids]);

                    const onVariantUpdate = (currentVariant: OpenApiSchemaProductVariantListItem) => {
                        if (updatedIds.has(currentVariant.id)) {
                            return {
                                ...currentVariant,
                                category_ids: currentVariant.category_ids
                                    .filter((categoryId) => !updatedCategories.has(categoryId))
                                    .concat(payload.add_category_ids),
                            };
                        }

                        return currentVariant;
                    };

                    // Обновляем данные в текущем запросе ручки для грида вариантов
                    if (variantGridTagInvalidator.lastArgs) {
                        updateVariantsInGridProductCache(variantGridTagInvalidator.lastArgs, {
                            onVariantUpdate,
                        });
                    }

                    // Обновляем данные в текущем запросе ручки для грида продуктов
                    if (productGridTagInvalidator.lastArgs) {
                        updateVariantsInGridProductCache(productGridTagInvalidator.lastArgs, {
                            onVariantUpdate,
                        });
                    }

                    // Обновляем данные в текущем запросе ручки для грида карточек вариантов
                    if (cardGridTagInvalidator.lastArgs) {
                        updateVariantsInGridProductCache(cardGridTagInvalidator.lastArgs, {
                            onVariantUpdate,
                        });
                    }
                },
            }),
            patchVariantsCardBulk: builder.mutation<
                OpenApiUpdateCardVariantsInBulkResponseSuccess,
                { body: OpenApiUpdateCardVariantsInBulkRequest; params: OpenApiUpdateCardVariantsInBulkPathParameters }
            >({
                query: ({ params: { id }, body }) => {
                    return {
                        url: `/variants/${id}/card_bulk`,
                        method: 'PATCH',
                        body,
                    };
                },
                invalidatesTags: (_, error, args) => {
                    if (error) {
                        return [];
                    }

                    return [
                        { type: ProductCardTag, id: args.params.id },
                        ...(args.body.name !== undefined || args.body.status !== undefined ? [VariantsCountTag] : []),
                    ];
                },
                async onQueryStarted(_, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data: updatedVariants } = queryFulfilledResult;

                    updatedVariants.map(updateVariantCache);
                },
            }),
            patchVariantsProductBulk: builder.mutation<
                OpenApiUpdateProductVariantsInBulkResponseSuccess,
                {
                    body: OpenApiUpdateProductVariantsInBulkRequest;
                    params: OpenApiUpdateProductVariantsInBulkPathParameters;
                }
            >({
                query: ({ params: { id }, body }) => {
                    return {
                        url: `/variants/${id}/product_bulk`,
                        method: 'PATCH',
                        body,
                    };
                },
                invalidatesTags: (_, error, args) => {
                    if (error) {
                        return [];
                    }

                    return [
                        { type: ProductCardTag, id: args.params.id },
                        ...(args.body.name !== undefined || args.body.status !== undefined ? [VariantsCountTag] : []),
                    ];
                },
                async onQueryStarted(_, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data: updatedVariants } = queryFulfilledResult;

                    updatedVariants.map(updateVariantCache);
                },
            }),

            mergePrevalidate: builder.mutation<
                OpenApiMergeProductsPreValidateResponseSuccess,
                OpenApiMergeProductsPreValidateRequest
            >({
                query: (payload) => {
                    return {
                        url: '/products/merge/prevalidate',
                        method: 'POST',
                        body: payload,
                    };
                },
            }),
            mergeValidate: builder.mutation<
                OpenApiMergeProductsValidateResponseSuccess,
                OpenApiMergeProductsValidateRequest
            >({
                query: (payload) => {
                    return {
                        url: '/products/merge/validate',
                        method: 'POST',
                        body: payload,
                    };
                },
            }),
            merge: builder.mutation<OpenApiMergeProductsV2ResponseSuccess, OpenApiMergeProductsV2Request>({
                query: (payload) => {
                    return {
                        url: '/products/merge/v2',
                        method: 'POST',
                        body: payload,
                    };
                },
                invalidatesTags: (_, error) => {
                    if (error) {
                        return [];
                    }

                    //т.к. товары могли сильно измениться, инвалидируем весь грид
                    return [CardsGridTag, VariantsGridTag];
                },
            }),
            unmerge: builder.mutation<
                OpenApiUnmergeProductResponseSuccess,
                {
                    params: { productId: string };
                    payload: OpenApiUnmergeProductRequest;
                }
            >({
                query: ({ params: { productId }, payload }) => {
                    return {
                        url: `/products/${productId}/unmerge`,
                        method: 'POST',
                        body: payload,
                    };
                },
                invalidatesTags: (_, error) => {
                    if (error) {
                        return [];
                    }

                    //т.к. товары могли сильно измениться, инвалидируем весь грид
                    return [CardsGridTag, VariantsGridTag];
                },
            }),
            unmergeVariants: builder.mutation<OpenApiUnmergeProductsResponseSuccess, OpenApiUnmergeProductsRequest>({
                query: (payload) => {
                    return {
                        url: `/products/unmerge`,
                        method: 'POST',
                        body: payload,
                    };
                },
                invalidatesTags: (_, error) => {
                    if (error) {
                        return [];
                    }

                    //т.к. товары могли сильно измениться, инвалидируем весь грид
                    return [CardsGridTag, VariantsGridTag];
                },
            }),
            getWarehouses: builder.query<OpenApiGetWarehousesResponseSuccess, OpenApiGetWarehousesRequest>({
                query: () => {
                    return {
                        url: '/warehouses',
                        method: 'GET',
                    };
                },
            }),
            postProduct: builder.mutation<OpenApiCreateProductResponseSuccess, OpenApiCreateProductRequest>({
                query: (payload) => {
                    return {
                        url: '/products',
                        method: 'POST',
                        body: payload,
                    };
                },
                invalidatesTags: (_, error) => {
                    if (error) {
                        return [];
                    }

                    //т.к. товары могли сильно измениться, инвалидируем весь грид
                    return [CardsGridTag, VariantsGridTag, CardsCountTag, VariantsCountTag];
                },
            }),
            postCardPageVariant: builder.mutation<
                OpenApiApplyPageVariantToProductCardsResponseSuccess,
                OpenApiApplyPageVariantToProductCardsRequest
            >({
                query: (payload) => {
                    return {
                        url: '/cards/page_variant',
                        method: 'POST',
                        body: payload,
                    };
                },
                invalidatesTags: (_variantIds, error) => {
                    if (error) {
                        return [];
                    }

                    return [
                        { type: ProductTag, id: 'getProduct' },
                        { type: VariantTag, id: 'getProductVariantById' },
                        { type: ProductCardTag, id: 'allProducts' },
                    ];
                },
                async onQueryStarted({ card_ids, page_id }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const updatedIds = new Set(card_ids);

                    // Инвалидируем грид вариантов
                    api.util.invalidateTags(variantGridTagInvalidator.getTagsToInvalidate());

                    const onVariantUpdate = (currentVariant: OpenApiSchemaProductVariantListItem) => {
                        if (updatedIds.has(currentVariant.product_card_id)) {
                            return { ...currentVariant, page_id };
                        }

                        return currentVariant;
                    };

                    // Обновляем данные в текущем запросе ручки для грида
                    if (variantGridTagInvalidator.lastArgs) {
                        updateVariantsInGridProductCache(variantGridTagInvalidator.lastArgs, {
                            onVariantUpdate,
                        });
                    }

                    // Инвалидируем грид архива
                    api.util.invalidateTags(archivedVariantGridTagInvalidator.getTagsToInvalidate());

                    if (productGridTagInvalidator.lastArgs) {
                        updateVariantsInGridProductCache(archivedVariantGridTagInvalidator.lastArgs, {
                            onVariantUpdate,
                        });
                    }

                    // Инвалидируем грид продукта
                    api.util.invalidateTags(productGridTagInvalidator.getTagsToInvalidate());

                    // Обновляем данные в текущем запросе ручки для грида
                    if (productGridTagInvalidator.lastArgs) {
                        updateVariantsInGridProductCache(productGridTagInvalidator.lastArgs, {
                            onVariantUpdate,
                        });
                    }

                    // Инвалидируем грид карточек вариантов
                    api.util.invalidateTags(cardGridTagInvalidator.getTagsToInvalidate());

                    // Обновляем данные в текущем запросе ручки для грида
                    if (cardGridTagInvalidator.lastArgs) {
                        updateVariantsInGridProductCache(cardGridTagInvalidator.lastArgs, {
                            onVariantUpdate,
                        });
                    }
                },
            }),
            generateSeo: builder.mutation<
                OpenApiGenerateAISeoForVariantResponseSuccess,
                { id: string; user_prompt: string }
            >({
                query: ({ id, user_prompt }) => {
                    return {
                        url: OpenApiGenerateAISeoForVariantUrl.replace('{id}', encodeURIComponent(id)),
                        method: OpenApiGenerateAISeoForVariantMethod,
                        body: { user_prompt },
                    };
                },
            }),
            bulkUpdateVariantMedia: builder.mutation<
                OpenApiBulkUpdateVariantMediaResponseSuccess,
                { body: OpenApiBulkUpdateVariantMediaRequest }
            >({
                query: ({ body }) => {
                    return {
                        url: getOpenApiBulkUpdateVariantMediaUrl(),
                        method: OpenApiBulkUpdateVariantMediaMethod,
                        body,
                    };
                },
                invalidatesTags: (_, error) => {
                    if (error) {
                        return [];
                    }

                    //т.к. товары могли сильно измениться, инвалидируем весь грид
                    return [CardsGridTag, VariantsGridTag];
                },
            }),
            getProductsCardSortOrderSettings: builder.query<
                OpenApiGetProductsCardSortOrderSettingsResponseSuccess,
                OpenApiGetProductsCardSortOrderSettingsRequest
            >({
                query: () => {
                    return {
                        url: OpenApiGetProductsCardSortOrderSettingsUrl,
                        method: OpenApiGetProductsCardSortOrderSettingsMethod,
                    };
                },
            }),
            patchProductsCardSortOrderSettings: builder.mutation<
                OpenApiPatchProductsCardSortOrderSettingsResponseSuccess,
                OpenApiPatchProductsCardSortOrderSettingsRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiPatchProductsCardSortOrderSettingsUrl,
                        method: OpenApiPatchProductsCardSortOrderSettingsMethod,
                        body: payload,
                    };
                },
                onQueryStarted: async (_, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data } = queryFulfilledResult;

                    api.endpoints.getProductsCardSortOrderSettings.updateQueryData(undefined, (draft) => {
                        return {
                            ...draft,
                            ...data,
                        };
                    });
                },
            }),
        };
    },
});

function updateVariantsInGridProductCache(
    args: { payload: OpenApiGetProductsByRequestRequest } | null,
    callbacks: {
        onVariantUpdate?: (
            currentVariant: OpenApiSchemaProductVariantListItem
        ) => OpenApiSchemaProductVariantListItem | undefined;
    }
) {
    const { onVariantUpdate } = callbacks;

    if (!args) {
        return;
    }

    api.endpoints.getArchivedVariantsForGrid.updateQueryData(args, (draft) => {
        if (draft) {
            const newVariants: OpenApiSchemaProductVariantListItem[] = [];

            let hasVariantChanges = false;

            // Пробегаемся по вариантам
            draft.items.forEach((variant: OpenApiSchemaProductVariantListItem) => {
                let updatedVariant: OpenApiSchemaProductVariantListItem | undefined = variant;

                if (onVariantUpdate) {
                    // Обновляем вариант по колбеку
                    updatedVariant = onVariantUpdate(variant);
                    hasVariantChanges = updatedVariant !== variant || hasVariantChanges;
                }

                // Если колбек вернул новый вариант, добавляем его в новый массив, иначе не добавляем
                if (updatedVariant && updatedVariant.status === 'ARCHIVED') {
                    newVariants.push(updatedVariant);
                }
            });

            if (hasVariantChanges) {
                draft.items = newVariants;
            }
        }
    });

    api.endpoints.getCardsForGrid.updateQueryData(args, (draft) => {
        if (draft) {
            let hasProductChanges = false;
            const newItems: OpenApiSchemaAggregatedProductCardListItem[] = [];

            // Пробегаемся по карточкам
            draft.cards.forEach((card) => {
                let hasVariantChanges = false;
                const newVariants: OpenApiSchemaProductVariantListItem[] = [];

                card.variants.forEach((variant) => {
                    let updatedVariant: OpenApiSchemaProductVariantListItem | undefined = variant;

                    if (onVariantUpdate) {
                        // Обновляем вариант по колбеку
                        updatedVariant = onVariantUpdate(variant);
                        hasVariantChanges = updatedVariant !== variant || hasVariantChanges;
                    }

                    // Если колбек вернул новый вариант, добавляем его в новый массив, иначе не добавляем
                    if (updatedVariant) {
                        newVariants.push(updatedVariant);
                    }
                });

                let updatedCard: OpenApiSchemaAggregatedProductCardListItem | undefined = card;

                if (hasVariantChanges) {
                    // Обновляем карточку если были изменения в вариантах
                    hasProductChanges = true;

                    // Пересчитываем количество опубликованных вариантов и скрытых
                    const visibility = {
                        published_count: newVariants.filter(({ status }) => status === 'PUBLISHED').length,
                        hidden_count: newVariants.filter(({ status }) => status === 'HIDDEN').length,
                    };

                    updatedCard = {
                        ...card,
                        visibility,
                        variants: newVariants,
                        category_ids: newVariants[0]?.category_ids ?? card.category_ids,
                        page_id: newVariants[0]?.page_id ?? card.page_id,
                    };
                }

                // Если колбек вернул новую карточку, добавляем ее в новый массив, иначе не добавляем
                if (updatedCard) {
                    newItems.push(updatedCard);
                }
            });

            if (hasProductChanges) {
                draft.cards = newItems;
            }
        }
    });

    api.endpoints.getVariantsForGrid.updateQueryData(args, (draft) => {
        if (draft) {
            const newVariants: OpenApiSchemaProductVariantListItem[] = [];

            let hasVariantChanges = false;

            // Пробегаемся по вариантам
            draft.items.forEach((variant) => {
                let updatedVariant: OpenApiSchemaProductVariantListItem | undefined = variant;

                if (onVariantUpdate) {
                    // Обновляем вариант по колбеку
                    updatedVariant = onVariantUpdate(variant);
                    hasVariantChanges = updatedVariant !== variant || hasVariantChanges;
                }

                // Если колбек вернул новый вариант, добавляем его в новый массив, иначе не добавляем
                if (updatedVariant) {
                    newVariants.push(updatedVariant);
                }
            });

            if (hasVariantChanges) {
                draft.items = newVariants;
            }
        }
    });
}

const fillCharacteristicFlags = <C extends Object>(
    characteristic: C
): C & {
    display_sequence: number;
    is_filterable: false;
    is_hidden: false;
} => {
    // Из-за несовпадения типов OpenApiSchemaProductVariantListItem и OpenApiSchemaProductVariantWithSettings выставляем значения по-умолчанию для недостающих полей
    return {
        is_filterable: false,
        is_hidden: false,
        display_sequence: 1,
        ...characteristic,
    };
};

function updateVariantCache(updatedVariant: OpenApiSchemaProductVariant) {
    // Обновляем ручку получения продукта
    api.endpoints.getProduct.updateQueryData({ productId: updatedVariant.product_id }, (draft) => {
        draft.variants = draft.variants.map((variant: OpenApiSchemaProductVariantWithSettings) => {
            const resultVariant = variant.id === updatedVariant.id ? updatedVariant : variant;

            return {
                ...resultVariant,
                deleted_category_ids: variant.deleted_category_ids,
                characteristics: (resultVariant.characteristics || []).map(
                    fillCharacteristicFlags<OpenApiSchemaProductCharacteristic>
                ),
            };
        });

        return draft;
    });

    // Обновляем ручку получения варианта
    api.endpoints.getProductVariantById.updateQueryData(
        { id: updatedVariant.id },
        (draft: OpenApiSchemaProductVariantWithSettings) => {
            return {
                ...updatedVariant,
                characteristics: (updatedVariant.characteristics || []).map(fillCharacteristicFlags),
                deleted_category_ids: draft.deleted_category_ids,
            };
        }
    );

    // Обновляем данные в текущем запросе ручки для грида
    if (productGridTagInvalidator.lastArgs) {
        updateVariantsInGridProductCache(productGridTagInvalidator.lastArgs, {
            onVariantUpdate: (currentVariant) => {
                if (currentVariant.id === updatedVariant.id) {
                    return {
                        addons: currentVariant.addons,
                        back_in_stock_requests_count: currentVariant.back_in_stock_requests_count,
                        favourites_count: currentVariant.favourites_count,
                        ...updatedVariant,
                        price_index_summary: getUpdatedPriceIndexSummary(currentVariant, updatedVariant),
                    };
                }

                return currentVariant;
            },
        });
    }

    // Инвалидируем грид вариантов
    api.util.invalidateTags(variantGridTagInvalidator.getTagsToInvalidate());

    // Обновляем данные в текущем запросе ручки для грида
    if (variantGridTagInvalidator.lastArgs) {
        updateVariantsInGridProductCache(variantGridTagInvalidator.lastArgs, {
            onVariantUpdate: (currentVariant) => {
                if (currentVariant.id === updatedVariant.id) {
                    return {
                        addons: currentVariant.addons,
                        back_in_stock_requests_count: currentVariant.back_in_stock_requests_count,
                        favourites_count: currentVariant.favourites_count,
                        ...updatedVariant,
                        price_index_summary: getUpdatedPriceIndexSummary(currentVariant, updatedVariant),
                    };
                }

                return currentVariant;
            },
        });
    }

    // Инвалидируем грид карточек
    api.util.invalidateTags(cardGridTagInvalidator.getTagsToInvalidate());

    // Обновляем данные в текущем запросе ручки для грида
    if (cardGridTagInvalidator.lastArgs) {
        updateVariantsInGridProductCache(cardGridTagInvalidator.lastArgs, {
            onVariantUpdate: (currentVariant) => {
                if (currentVariant.id === updatedVariant.id) {
                    return {
                        addons: currentVariant.addons,
                        back_in_stock_requests_count: currentVariant.back_in_stock_requests_count,
                        favourites_count: currentVariant.favourites_count,
                        ...updatedVariant,
                        price_index_summary: getUpdatedPriceIndexSummary(currentVariant, updatedVariant),
                    };
                }

                return currentVariant;
            },
        });
    }
}

export const getProductQuery = api.endpoints.getProduct.query;
export const useLazyGetProduct = api.useLazyGetProductQuery;
export const updateProductVariant = api.endpoints.updateProductVariant.mutate;
export const useProductCards = api.useGetProductCardsQuery;
export const useLightProductCards = api.useGetLightProductCardsQuery;
export const useLazyLightProductCards = api.useLazyGetLightProductCardsQuery;
export const patchProductVariantsCardBulk = api.endpoints.patchVariantsCardBulk.mutate;
export const patchProductVariantsProductBulk = api.endpoints.patchVariantsProductBulk.mutate;
export const postProduct = api.endpoints.postProduct.mutate;

export const updateProductVariantsStatuses = api.endpoints.updateProductVariantsStatuses.mutate;
export const updateProductsCategories = api.endpoints.updateProductsCategories.mutate;

export const mergeVariantsInProductPrevalidate = api.endpoints.mergePrevalidate.mutate;
export const mergeVariantsInProductValidate = api.endpoints.mergeValidate.mutate;
export const mergeVariantsInProduct = api.endpoints.merge.mutate;
export const unmergeVariantsInProduct = api.endpoints.unmerge.mutate;
export const unmergeProductVariants = api.endpoints.unmergeVariants.mutate;

export const useGetProductCardsCount = api.useGetCardsCountQuery;
export const useGetProductVariantsCount = api.useGetVariantsCountQuery;
export const useGetProductCountAll = api.useGetProductCountAllQuery;
export const useLazyGetProductCountAll = api.useLazyGetProductCountAllQuery;
export const useSearchProductVariants = api.useGetVariantsForGridQuery;
export const useLazySearchProductVariants = api.useLazyGetVariantsForGridQuery;

export const postProductCardPageVariant = api.endpoints.postCardPageVariant.mutate;

export const setVariantsRequiresMarking = api.endpoints.setVariantsRequiresMarking.mutate;

export const deleteProductVariantExternalIDs = api.endpoints.deleteProductVariantExternalIDs.mutate;

export const deletePermanentlyProductVariants = api.endpoints.deletePermanentlyProductVariants.mutate;

export const bulkUpdateProductVariantMedia = api.endpoints.bulkUpdateVariantMedia.mutate;

export type ProductVariantSortByKey = Required<OpenApiGetProductVariantsByRequestRequest>['sort_by'];

export const useGetProductWarehouses = api.useGetWarehousesQuery;

export const invalidateProductGrids = () =>
    api.util.invalidateTags([CardsGridTag, VariantsGridTag, ArchivedVariantsGridTag]);

export const invalidateProductCardsGrid = () => api.util.invalidateTags([CardsGridTag]);

export const generateProductSeo = api.endpoints.generateSeo.mutate;

export const invalidateProductCounts = () => {
    api.util.invalidateTags([CardsCountTag, VariantsCountTag]);
};

export const useGetProductsCardSortOrderSettings = api.useGetProductsCardSortOrderSettingsQuery;
export const usePatchProductsCardSortOrderSettings = api.usePatchProductsCardSortOrderSettingsMutation;
